import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import type { AppDispatch } from 'app/store';

import {
  getPharmacy,
  getUserPharmacies,
  getPharmacyPendingAppointmentsCount,
  getPharmacyStats,
  getPharmacyMembers,
  getPharmacyPatientHelpStats,
  getPharmacyLinkRequests,
} from 'app/services/pharmacies';
import { getOrderStats } from 'app/services/order';

import SelectedPharmacy, { type PharmacyResponse } from './selectedPharmacy';

/* eslint-disable camelcase */
type PharmacySummary = { _id: string } & Record<string, unknown>;

type PharmacyStatsState = {
  requests: unknown;
  orders: unknown;
  appointments: unknown;
  messages: unknown;
  patientHelp?: unknown;
};

type RootState = {
  pharmacySelector: PharmacySelectorState;
  auth: {
    user?: {
      roles?: string[];
      pharmacies?: string[];
    };
  };
};

type AppThunk = (dispatch: AppDispatch, getState: () => RootState) => Promise<void>;

export type PharmacySelectorState = {
  error: SerializedError | null;
  fetching: boolean;
  pharmacies: PharmacySummary[] | undefined;
  selectedPharmacyId: string | null;
  selectedPharmacy: SelectedPharmacy | null;
  pharmacyStats: PharmacyStatsState;
  teamMembers: unknown[];
};
/* eslint-enable camelcase */

export const fetchUserPharmacies = createAsyncThunk<PharmacySummary[], string>(
  'pharmacySelector/fetchUserPharmacies',
  async (userId: string) => {
    const response = await getUserPharmacies({ userId });
    return response;
  }
);

export const fetchOrderStats = (): AppThunk => {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const pharmacyId = getState().pharmacySelector.selectedPharmacyId;
    if (pharmacyId) {
      const orderStats = await getOrderStats({ pharmacyId });
      dispatch(setPharmacyOrderStats(orderStats));
    }
  };
};

export const fetchAppointmentStats = (): AppThunk => {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const pharmacyId = getState().pharmacySelector.selectedPharmacyId;
    if (pharmacyId) {
      const appointmentStats = await getPharmacyPendingAppointmentsCount({ pharmacyId });
      dispatch(setPharmacyAppointmentStats(appointmentStats));
    }
  };
};

export const fetchMessageStats = (): AppThunk => {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const pharmacyId = getState().pharmacySelector.selectedPharmacyId;
    if (pharmacyId) {
      const messageStats = await getPharmacyStats({ pharmacyId });
      dispatch(setPharmacyMessageStats(messageStats));
    }
  };
};

export const fetchRequestsStats = (): AppThunk => {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const pharmacyId = getState().pharmacySelector.selectedPharmacyId;
    if (pharmacyId) {
      const { requestsTotal } = await getPharmacyLinkRequests({
        pharmacyId,
        page: 0,
        limit: 1,
        orderBy: null,
      });
      dispatch(setPharmacyRequestsStats({ totalRequestsCount: requestsTotal }));
    }
  };
};

export const fetchPatientHelpStats = (): AppThunk => {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const pharmacyId = getState().pharmacySelector.selectedPharmacyId;
    if (pharmacyId) {
      const patientHelpStats = await getPharmacyPatientHelpStats({ pharmacyId });
      dispatch(setPharmacyPatientHelpStats(patientHelpStats));
    }
  };
};

export const fetchPharmacyTeamMembers = (): AppThunk => {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const pharmacyId = getState().pharmacySelector.selectedPharmacyId;
    if (pharmacyId) {
      const members = await getPharmacyMembers({ pharmacyId });
      dispatch(setPharmacyMembers(members));
    }
  };
};

export const changeSelectedPharmacy = (pharmacyId?: string): AppThunk => {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const { pharmacies, selectedPharmacyId: currentSelectedPharmacyId } =
      getState().pharmacySelector;
    const { user } = getState().auth;

    let selectedPharmacyId = pharmacyId || localStorage.getItem('selected_pharmacy_id');
    if (!selectedPharmacyId && !pharmacies?.length) return;

    const isSupportUser = user?.roles?.includes('support');

    if (!isSupportUser && !pharmacies?.length) {
      const allowedPharmacies = user?.pharmacies || [];
      if (selectedPharmacyId && !allowedPharmacies.includes(selectedPharmacyId)) return;
    }

    if (pharmacies?.length) {
      const pharmacyIdsFromSelector = pharmacies.map((pharmacy) => pharmacy._id);
      if (!selectedPharmacyId || !pharmacyIdsFromSelector.includes(selectedPharmacyId)) {
        // eslint-disable-next-line prefer-destructuring
        selectedPharmacyId = pharmacyIdsFromSelector[0];
      }
    }

    if (!selectedPharmacyId || selectedPharmacyId === currentSelectedPharmacyId) return;

    try {
      const pharmacy = await getPharmacy({ pharmacyId: selectedPharmacyId });
      dispatch(resetPharmacyStats());
      dispatch(setPharmacy(pharmacy));
    } catch (error) {
      dispatch(setError(new Error('Failed to load pharmacy')));
    }
  };
};

function createInitialPharmacyStatsState(): PharmacyStatsState {
  return {
    requests: {},
    orders: {},
    appointments: {},
    messages: {},
  };
}

const initialState: PharmacySelectorState = {
  error: null,
  fetching: false,
  pharmacies: undefined,
  selectedPharmacyId: null,
  selectedPharmacy: null,
  pharmacyStats: createInitialPharmacyStatsState(),
  teamMembers: [],
};

const pharmacySelectorSlice = createSlice({
  name: 'pharmacySelector',
  initialState,
  reducers: {
    setPharmacy(state, action: PayloadAction<PharmacyResponse>) {
      const pharmacy = action.payload;
      localStorage.setItem('selected_pharmacy_id', pharmacy._id);
      state.selectedPharmacyId = pharmacy._id;
      state.selectedPharmacy = new SelectedPharmacy(pharmacy);
    },
    setPaymentOverdue(state, action: PayloadAction<boolean>) {
      if (!state.selectedPharmacy) return;
      const updated: PharmacyResponse = {
        ...state.selectedPharmacy,
        chain: {
          ...state.selectedPharmacy.chain,
          saas_billing: {
            ...state.selectedPharmacy.chain?.saas_billing,
            payment_overdue: action.payload,
          },
        },
      };
      state.selectedPharmacy = new SelectedPharmacy(updated);
    },
    setError(state, action: PayloadAction<Error>) {
      state.error = action.payload;
    },
    resetPharmacyStats(state) {
      state.pharmacyStats = createInitialPharmacyStatsState();
    },
    setPharmacyOrderStats(state, action: PayloadAction<unknown>) {
      state.pharmacyStats.orders = action.payload;
    },
    setPharmacyAppointmentStats(state, action: PayloadAction<unknown>) {
      state.pharmacyStats.appointments = action.payload;
    },
    setPharmacyMessageStats(state, action: PayloadAction<unknown>) {
      state.pharmacyStats.messages = action.payload;
    },
    setPharmacyRequestsStats(state, action: PayloadAction<unknown>) {
      state.pharmacyStats.requests = action.payload;
    },
    setPharmacyPatientHelpStats(state, action: PayloadAction<unknown>) {
      state.pharmacyStats.patientHelp = action.payload;
    },
    setPharmacyMembers(state, action: PayloadAction<unknown[]>) {
      state.teamMembers = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserPharmacies.pending, (state) => {
      state.fetching = true;
    });

    builder.addCase(
      fetchUserPharmacies.fulfilled,
      (state, action: PayloadAction<PharmacySummary[]>) => {
        state.fetching = false;
        state.pharmacies = action.payload;
      }
    );

    builder.addCase(fetchUserPharmacies.rejected, (state, action) => {
      state.fetching = false;
      state.error = action.error;
    });
  },
});

export const {
  setPharmacy,
  setPaymentOverdue,
  setError,
  resetPharmacyStats,
  setPharmacyOrderStats,
  setPharmacyAppointmentStats,
  setPharmacyMessageStats,
  setPharmacyRequestsStats,
  setPharmacyMembers,
  setPharmacyPatientHelpStats,
} = pharmacySelectorSlice.actions;

export default pharmacySelectorSlice.reducer;
